import {
  buildTradePeriodLocalRangeQuery,
  compareCalendarDateStrings,
  formatDateAsCalendarDayInTimeZone,
  formatDateAsCalendarDayLocal,
  resolveTradeListWindowQuery,
  subtractCalendarDaysFromYmd,
  type TradeListWindowQuery,
} from "./notificationPeriod";

/**
 * Backend / product “all-time” for reports & simulate is a rolling ~365-day lookback,
 * not true history from year 2000. See `TIMEFRAME_LOOKBACK_DAYS` in candleTimeframeBarBudget.
 */
export const REPORT_ALL_TIME_LOOKBACK_DAYS = 365;

export type ReportTradeReviewCohortPeriod = {
  local_start_date?: string | null;
  local_end_date?: string | null;
} | null | undefined;

/**
 * Resolve the closed-trade fetch / chart window for report Trade Review so it matches
 * the report cohort the simulator uses (not “all account history through created_at”).
 *
 * Priority:
 * 1. Frozen `cohort_period` local dates (clamped to as-of day when set)
 * 2. Explicit report start/end dates
 * 3. Timeframe presets resolved relative to as-of day (or today), with all-time = ~365 days
 */
export function resolveReportTradeReviewFetchWindow(opts: {
  dateRange: { startDate: Date | null; endDate: Date | null };
  timeframe: string;
  reportingTimeZone: string;
  asOfYmd?: string | null;
  cohortPeriod?: ReportTradeReviewCohortPeriod;
}): TradeListWindowQuery {
  const zone = opts.reportingTimeZone?.trim() || "UTC";
  const asOfYmd = opts.asOfYmd?.trim() || null;
  const periodOpts = {
    reportingTimeZone: zone,
    includeTimezoneQuery: true as const,
  };

  const clampEnd = (end: string): string => {
    if (asOfYmd && compareCalendarDateStrings(end, asOfYmd) > 0) return asOfYmd;
    return end;
  };

  const cohortStart = opts.cohortPeriod?.local_start_date?.trim() ?? "";
  const cohortEnd = opts.cohortPeriod?.local_end_date?.trim() ?? "";
  if (
    /^\d{4}-\d{2}-\d{2}$/.test(cohortStart) &&
    /^\d{4}-\d{2}-\d{2}$/.test(cohortEnd)
  ) {
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(cohortStart, clampEnd(cohortEnd), periodOpts),
    };
  }

  if (opts.dateRange.startDate && opts.dateRange.endDate) {
    const startYmd = formatDateAsCalendarDayLocal(opts.dateRange.startDate);
    const endYmd = clampEnd(formatDateAsCalendarDayLocal(opts.dateRange.endDate));
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(startYmd, endYmd, periodOpts),
    };
  }

  const endYmd = asOfYmd ?? formatDateAsCalendarDayInTimeZone(new Date(), zone);
  const tf = (opts.timeframe || "all-time").trim().toLowerCase();

  if (tf === "today") {
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(endYmd, endYmd, periodOpts),
    };
  }

  if (tf === "7-days" || tf === "14-days" || tf === "30-days") {
    const days = tf === "7-days" ? 7 : tf === "14-days" ? 14 : 30;
    const startYmd = subtractCalendarDaysFromYmd(endYmd, days, zone);
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(startYmd, endYmd, periodOpts),
    };
  }

  // all-time (and unknown): rolling lookback ending on as-of / today — same as simulate.
  const startYmd = subtractCalendarDaysFromYmd(endYmd, REPORT_ALL_TIME_LOOKBACK_DAYS, zone);
  return {
    kind: "local",
    period: buildTradePeriodLocalRangeQuery(startYmd, endYmd, periodOpts),
  };
}

/** Chart / request-window form of the same cohort (local YMD + timezone). */
export function resolveReportTradeReviewRequestLocalWindow(opts: {
  dateRange: { startDate: Date | null; endDate: Date | null };
  timeframe: string;
  reportingTimeZone: string;
  asOfYmd?: string | null;
  cohortPeriod?: ReportTradeReviewCohortPeriod;
}): {
  local_start_date: string;
  local_end_date: string;
  timezone: string;
} {
  const win = resolveReportTradeReviewFetchWindow(opts);
  if (win.kind === "local") {
    return {
      local_start_date: win.period.local_start_date,
      local_end_date: win.period.local_end_date,
      timezone: win.period.timezone ?? opts.reportingTimeZone,
    };
  }
  // Should not happen for resolveReportTradeReviewFetchWindow — keep a safe fallback.
  const fallback = resolveTradeListWindowQuery(
    opts.dateRange,
    opts.timeframe || "all-time",
    opts.reportingTimeZone,
    { includeTimezoneQuery: true }
  );
  if (fallback.kind === "local") {
    return {
      local_start_date: fallback.period.local_start_date,
      local_end_date: fallback.period.local_end_date,
      timezone: fallback.period.timezone ?? opts.reportingTimeZone,
    };
  }
  const zone = opts.reportingTimeZone?.trim() || "UTC";
  const endYmd =
    opts.asOfYmd?.trim() || formatDateAsCalendarDayInTimeZone(new Date(), zone);
  const startYmd = subtractCalendarDaysFromYmd(endYmd, REPORT_ALL_TIME_LOOKBACK_DAYS, zone);
  return { local_start_date: startYmd, local_end_date: endYmd, timezone: zone };
}
